/* global React */
// Home page sections (everything BELOW the approved hero — hero stays untouched).
// Premium "Ethereal Glass" aesthetic flowing from the hero's navy + aqua palette.
// Asymmetrical Bento + Editorial Split + Z-Axis Cascade layouts.
// Double-Bezel cards (outer shell + inner core, concentric radii).
// Custom cubic-bezier motion. Scroll-fade-up with blur.
// All copy is DRAFT — Dr. Esraa to approve.

const { useEffect, useRef, useState } = React;

/* ── Scroll-reveal: fade-up + blur on intersection ── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('dz-reveal--in');
          obs.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

/* ── Animated number with decimals + locale + prefix support.
   Self-observes via IntersectionObserver — animates only when in view.
   Re-runs on remount (e.g. via key prop on parent for tab-switching). ── */
function AnimatedNumber({ target, decimals = 0, prefix = '', delay = 0, duration = 1400 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        const start = performance.now();
        const tick = (now) => {
          const elapsed = now - start - delay;
          if (elapsed < 0) {
            raf = requestAnimationFrame(tick);
            return;
          }
          const t = Math.min(1, elapsed / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          setVal(target * eased);
          if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        obs.unobserve(el);
      }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [target, decimals, delay, duration]);

  let display;
  if (decimals > 0) {
    const divisor = Math.pow(10, decimals);
    display = (val / divisor).toFixed(decimals);
  } else {
    display = Math.round(val).toLocaleString('en-US');
  }
  return <span ref={ref}>{prefix}{display}</span>;
}

/* ── Animated counter ── */
function CountUp({ end, suffix = '', duration = 2000 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const startTs = performance.now();
        const tick = (now) => {
          const t = Math.min(1, (now - startTs) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          setVal(Math.round(end * eased));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
}

/* ── Reusable arrow icon ── */
const ArrowRight = (props) => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" {...props}>
    <path d="M3 8h9M8 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ──────────────────────────────────────────────────────────────────────
   2 · GeoAnswerParagraph — Editorial Index (Mockup B)
   Massive 3-word punch + numbered service index (TOC-style).
   ────────────────────────────────────────────────────────────────────── */
function GeoAnswer() {
  const data = window.DZ_HOME.approach;
  const SHOWN = data.services.length;
  const remaining = data.totalServices - SHOWN;
  return (
    <section className="dz-geo-answer" aria-label={data.sectionAriaLabel}>
      {/* Section header — sits in editorial space ABOVE the card */}
      <div className="dz-geo-answer__header">
        <span className="dz-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-geo-answer__title">
          {data.titleLeading} <em>{data.titleAccent}</em>
        </h2>
        <p className="dz-geo-answer__sub" dangerouslySetInnerHTML={{ __html: data.subtitle }} />
      </div>

      {/* Card — pure service index, full width */}
      <div className="dz-geo-answer__inner">
        <ul className="dz-geo-index">
          {data.services.map((s) => (
            <li key={s.num} className="dz-geo-index__row">
              <span className="dz-geo-index__num">{s.num}</span>
              <span className="dz-geo-index__name">{s.name}</span>
              <span className="dz-geo-index__desc">{s.desc}</span>
            </li>
          ))}
        </ul>
        <a href={data.moreHref} className="dz-geo-index__more" aria-label={`Explore all ${data.totalServices} services`}>
          <span className="dz-geo-index__more-label">{data.moreLabel}</span>
          <span className="dz-geo-index__more-meta">
            {(data.moreMetaTpl || '{shown} of {total} shown · +{remaining} more across {stages} stages')
              .replace('{shown}',     SHOWN)
              .replace('{total}',     data.totalServices)
              .replace('{remaining}', remaining)
              .replace('{stages}',    data.totalStages)}
          </span>
          <span className="dz-geo-index__more-icon"><ArrowRight /></span>
        </a>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   3 · Project Showcase Slider — 3 cards visible, prev/next arrows + dots.
   Real Saudi giga-projects (AMAALA, Red Sea, Qiddiya, Royal Commission for
   Riyadh) and major Jeddah landmarks, sourced from designzone.com.sa.
   ────────────────────────────────────────────────────────────────────── */
function ClientLogos() {
  // Project showcase data (8 items) lives in content/home.js as
  // window.DZ_HOME.projects. Edit projects + section copy there.
  const data = window.DZ_HOME.projects;
  const projects = data.items;

  const [page, setPage] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(3);
  const trackRef = useRef(null);

  // Responsive cards per view (1 / 2 / 3 by viewport width)
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w <= 600) setCardsPerView(1);
      else if (w <= 900) setCardsPerView(2);
      else setCardsPerView(3);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  const totalPages = Math.ceil(projects.length / cardsPerView);

  // Clamp page when totalPages drops on resize
  useEffect(() => {
    if (page >= totalPages) setPage(Math.max(0, totalPages - 1));
  }, [totalPages, page]);

  // Pixel-based translate. Use offsetWidth (logical CSS pixels) — NOT
  // getBoundingClientRect (which returns post-zoom physical pixels). The
  // body has zoom:0.8 from colors_and_type.css, so mixing scales would
  // under-shift each page and leave partial cards visible.
  useEffect(() => {
    if (!trackRef.current) return;
    const cards = trackRef.current.querySelectorAll('.dz-clients__card');
    if (cards.length === 0) return;
    const cardW = cards[0].offsetWidth;
    const gapPx = parseFloat(getComputedStyle(trackRef.current).columnGap) || 16;
    const slide = page * cardsPerView * (cardW + gapPx);
    trackRef.current.style.transform = `translateX(${-slide}px)`;
  }, [page, cardsPerView]);

  const goTo = (i) => setPage(Math.max(0, Math.min(totalPages - 1, i)));

  return (
    <section className="dz-clients" aria-label={data.sectionAriaLabel}>
      <div className="dz-clients__inner">

        <div className="dz-clients__head">
          <div className="dz-clients__head-left">
            <span className="dz-eyebrow">{data.eyebrow}</span>
            <h2 className="dz-clients__title">
              {data.titleLeading} <em>{data.titleAccent}</em>
            </h2>
          </div>
          <div className="dz-clients__meta">
            <span className="dz-clients__meta-num">{data.metaNum}</span>
            <span className="dz-clients__meta-label">{data.metaLabel}</span>
          </div>
        </div>

        <div className="dz-clients__controls">
          <div className="dz-clients__progress">
            <span>{String(page + 1).padStart(2, '0')} / {String(totalPages).padStart(2, '0')}</span>
            <div className="dz-clients__progress-track">
              <div className="dz-clients__progress-fill" style={{ width: `${((page + 1) / totalPages) * 100}%` }} />
            </div>
          </div>
          <div className="dz-clients__nav">
            <button
              className="dz-clients__nav-btn"
              onClick={() => goTo(page - 1)}
              disabled={page === 0}
              aria-label={data.prevLabel}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <button
              className="dz-clients__nav-btn"
              onClick={() => goTo(page + 1)}
              disabled={page === totalPages - 1}
              aria-label={data.nextLabel}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>
        </div>

        <div className="dz-clients__viewport">
          <div className="dz-clients__track" ref={trackRef}>
            {projects.map((p, i) => (
              <article key={i} className="dz-clients__card">
                <div>
                  <div className="dz-clients__card-top">
                    <div className="dz-clients__card-tag-wrap">
                      <span className="dz-clients__card-tag">{p.tag}</span>
                      <span className="dz-clients__card-year">{p.year}</span>
                    </div>
                    <div className="dz-clients__card-logo">
                      {p.logo.src
                        ? <img src={p.logo.src} alt={p.logo.alt} loading="lazy" />
                        : <span className="dz-clients__card-logo-mark">{p.logo.mark}</span>}
                    </div>
                  </div>
                  <h3 className="dz-clients__card-name">{p.name}</h3>
                  <p className="dz-clients__card-scope">{p.scope}</p>
                </div>
                <div className="dz-clients__card-stats">
                  {p.stats.map((s, j) => (
                    <div key={j} className="dz-clients__card-stat">
                      <span className="dz-clients__card-stat-num">{s.num}</span>
                      <span className="dz-clients__card-stat-label">{s.label}</span>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="dz-clients__dots">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              className={`dz-clients__dot${i === page ? ' dz-clients__dot--active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Go to page ${i + 1}`}
            />
          ))}
        </div>

        <div className="dz-clients__strip">
          <div className="dz-clients__strip-label">
            <span className="dz-clients__strip-eyebrow">{data.strip.eyebrow}</span>
            <span className="dz-clients__strip-title">{data.strip.title}</span>
          </div>
          {data.strip.cells.map((c, i) => (
            <div key={i} className="dz-clients__strip-cell">
              <div className="dz-clients__strip-num">{c.num}</div>
              <div className="dz-clients__strip-sub">{c.label}</div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   3b · Our work — every project as an image card linking to its page.
   Category chips filter the grid; the first few show, the rest open with
   "Show all". Data in content/home.js (window.DZ_HOME.featuredProjects).
   ────────────────────────────────────────────────────────────────────── */
function FeaturedProjects() {
  const data = window.DZ_HOME.featuredProjects;
  const strip = window.DZ_HOME.projects && window.DZ_HOME.projects.strip;
  const ref = useReveal();
  const [cat, setCat] = useState('all');
  const [expanded, setExpanded] = useState(false);
  if (!data) return null;
  const counts = data.items.reduce((m, p) => { m[p.category] = (m[p.category] || 0) + 1; return m; }, {});
  const list = cat === 'all' ? data.items : data.items.filter((p) => p.category === cat);
  const limit = data.initialCount || 6;
  const shown = expanded ? list : list.slice(0, limit);
  const chips = [{ id: 'all', label: data.allFilter, n: data.items.length }].concat(
    data.categories.filter((c) => counts[c.id]).map((c) => ({ id: c.id, label: c.label, n: counts[c.id] })));
  return (
    <section className="dz-featured" aria-label={data.sectionAriaLabel}>
      <div className="dz-featured__inner dz-reveal" ref={ref}>
        <div className="dz-featured__head">
          <div className="dz-featured__head-left">
            <span className="dz-eyebrow">{data.eyebrow}</span>
            <h2 className="dz-featured__title">{data.titleLeading} <em>{data.titleAccent}</em></h2>
          </div>
          <p className="dz-featured__lead">{data.lead}</p>
        </div>
        {strip && (
          <div className="dz-featured__stats">
            <div className="dz-featured__stats-label">
              <span className="dz-featured__stats-eyebrow">{strip.eyebrow}</span>
              <span className="dz-featured__stats-title">{strip.title}</span>
            </div>
            {strip.cells.map((c, i) => (
              <div key={i} className="dz-featured__stat">
                <div className="dz-featured__stat-num">{c.num}</div>
                <div className="dz-featured__stat-label">{c.label}</div>
              </div>
            ))}
          </div>
        )}
        <div className="dz-featured__chips" role="group" aria-label={data.filterLabel}>
          {chips.map((c) => (
            <button key={c.id} type="button" className={'dz-featured__chip' + (cat === c.id ? ' is-on' : '')} aria-pressed={cat === c.id}
              onClick={() => { setCat(c.id); setExpanded(false); }}>
              {c.label} <span className="dz-featured__chip-n">{c.n}</span>
            </button>
          ))}
        </div>
        <div className="dz-featured__grid">
          {shown.map((p) => (
            <a key={p.slug} className="dz-featured__card" href={p.href}>
              <div className="dz-featured__media">
                <picture>
                  <source type="image/avif" srcSet={`${p.img.small} 800w, ${p.img.large} 1600w`} sizes="(max-width: 600px) 92vw, (max-width: 960px) 46vw, 420px" />
                  <img src={p.img.fallback} alt={p.alt} loading="lazy" decoding="async" width="800" height="500" />
                </picture>
              </div>
              <div className="dz-featured__body">
                <span className="dz-featured__tag">{p.tag}</span>
                <h3 className="dz-featured__name">{p.name}</h3>
                <p className="dz-featured__desc">{p.desc}</p>
                <span className="dz-featured__link">{data.linkLabel} <ArrowRight className="dz-featured__arrow" /></span>
              </div>
            </a>
          ))}
        </div>
        <div className="dz-featured__foot">
          {list.length > limit && (
            <button type="button" className="dz-featured__more" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
              {expanded ? data.lessLabel : data.moreLabel.replace('{n}', list.length)}
            </button>
          )}
          <a className="dz-featured__all" href={data.pageHref}>{data.pageLabel} <ArrowRight className="dz-featured__arrow" /></a>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   4 · "What is BIM" answer block — Editorial Split
   ────────────────────────────────────────────────────────────────────── */
function WhatIsBIM() {
  const ref = useReveal();
  return (
    <section className="dz-section dz-reveal" ref={ref} id="what-is-bim">
      <div className="dz-section__inner dz-split">
        <div className="dz-split__head">
          <span className="dz-eyebrow">DEFINITION</span>
          <h2 className="dz-h2">What is <em>Building Information Modeling</em> (BIM)?</h2>
        </div>
        <div className="dz-split__body">
          <p>
            <strong>Building Information Modeling</strong> is a digital methodology where
            architects, engineers, and contractors collaborate on a single 3D federated model
            that contains every geometric, material, performance, and cost attribute of a built
            asset. Each object in the model carries data &mdash; not just shape &mdash; allowing
            teams to coordinate disciplines, simulate construction, validate code compliance,
            and forecast cost and time before a single brick is laid.
          </p>
          <p>
            In Saudi Arabia, BIM is the mandatory delivery standard for Vision&nbsp;2030
            giga-projects under <em>ISO&nbsp;19650</em>. Design Zone implements BIM across the
            full project lifecycle &mdash; from <em>EIR authoring and BEP</em> at programming,
            through LOD&nbsp;300 multi-discipline modeling and clash detection, to
            <em> digital twin handover</em> and facility management integration.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   5 · ServicesGrid — Asymmetrical Bento (1 wide + 6 standard)
   ────────────────────────────────────────────────────────────────────── */
function ServicesGrid() {
  const ref = useReveal();
  const featured = {
    icon: 'BIM',
    h3: 'BIM Modeling & Coordination',
    desc: 'Multi-discipline LOD 300+ federated models with clash detection, BIM coordination, and ISO 19650-aligned information management. Our flagship service — covering architectural, structural, and MEP authoring with full clash-detection workflows.',
    href: '/services/bim',
  };
  const services = [
    { icon: 'MEP', h3: 'MEP BIM Coordination',         desc: 'Mechanical, electrical, plumbing routing — clash-free.', href: '/services/bim/mep' },
    { icon: 'S2B', h3: 'Scan to BIM',                   desc: 'Point-cloud-to-BIM for as-built and heritage capture.', href: '/services/scan-to-bim' },
    { icon: 'VDC', h3: 'Virtual Design & Construction', desc: '4D scheduling + 5D cost from the federated model.',     href: '/services/vdc' },
    { icon: 'ISO', h3: 'ISO 19650 Implementation',      desc: 'EIR, BEP, and digital delivery standards setup.',       href: '/services/iso-19650' },
    { icon: 'A+S', h3: 'Architectural & Structural BIM', desc: 'Discipline-specific LOD 300 modeling + shop drawings.', href: '/services/bim/architecture' },
    { icon: 'FM',  h3: 'Facility Management & COBie',   desc: 'Asset registry, COBie data, CAFM/FM integration.',      href: '/services/bim/facility-management' },
  ];
  return (
    <section className="dz-section dz-reveal" ref={ref} aria-label="Services">
      <div className="dz-section__inner">
        <span className="dz-eyebrow">WHAT WE DO</span>
        <h2 className="dz-h2">BIM, Digital Twin &amp; VDC <em>across the full lifecycle</em></h2>
        <p className="dz-lead">
          Seven core service families covering every stage from programming brief to demolition
          and site restoration. Each ISO&nbsp;19650-aligned and Vision&nbsp;2030-ready.
        </p>
        <div className="dz-services__bento">
          {/* Featured full-width hero card — single row, horizontal layout */}
          <div className="dz-service dz-service--featured">
            <a href={featured.href} className="dz-bezel dz-service-card dz-service-card--featured">
              <div className="dz-bezel__core">
                <div className="dz-service-card__icon">{featured.icon}</div>
                <div>
                  <h3 className="dz-service-card__h3">{featured.h3}</h3>
                  <p className="dz-service-card__desc">{featured.desc}</p>
                </div>
                <span className="dz-service-card__arrow">
                  Learn more
                  <span className="dz-service-card__arrow-pill"><ArrowRight /></span>
                </span>
              </div>
            </a>
          </div>
          {/* 6 standard cards — 3 cols × 2 rows */}
          {services.map((s) => (
            <div key={s.href} className="dz-service">
              <a href={s.href} className="dz-bezel dz-service-card">
                <div className="dz-bezel__core">
                  <div className="dz-service-card__icon">{s.icon}</div>
                  <h3 className="dz-service-card__h3">{s.h3}</h3>
                  <p className="dz-service-card__desc">{s.desc}</p>
                  <span className="dz-service-card__arrow">
                    Learn more
                    <span className="dz-service-card__arrow-pill"><ArrowRight /></span>
                  </span>
                </div>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   6 · StatsBar — minimal, hairline dividers
   ────────────────────────────────────────────────────────────────────── */
function Stats() {
  const ref = useReveal();
  const stats = [
    { value: 55,    suffix: '+', label: 'Projects Delivered' },
    { value: 11,    suffix: '',  label: 'BIM Lifecycle Stages Covered' },
    { value: 15,    suffix: '',  label: 'Vision 2030 Giga-Projects' },
    { value: 19650, suffix: '',  label: 'ISO Certified' },
  ];
  return (
    <section className="dz-section dz-reveal" ref={ref} aria-label="Key statistics" style={{ paddingTop: 'clamp(56px, 7vw, 96px)', paddingBottom: 'clamp(56px, 7vw, 96px)' }}>
      <div className="dz-section__inner">
        <div className="dz-stats__grid">
          {stats.map((s, i) => (
            <div key={i} className="dz-stat">
              <div className="dz-stat__value"><CountUp end={s.value} suffix={s.suffix} /></div>
              <div className="dz-stat__label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   7 · Vision 2030 answer block — Editorial Split
   ────────────────────────────────────────────────────────────────────── */
function Vision2030Block() {
  const ref = useReveal();
  const data = window.DZ_HOME.vision2030;
  // Pillar icons stay in JSX (visual structure) — matched to data.pillars by index.
  const pillarIcons = [
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M9 12l2 2 4-4M12 3l9 4v6a9 9 0 0 1-9 9 9 9 0 0 1-9-9V7l9-4z" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 2L4 6v12l8 4 8-4V6l-8-4zM4 6l8 4 8-4M12 22V10" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 21h18M5 21V7l7-4 7 4v14" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ];

  return (
    <section className="dz-vision dz-reveal" ref={ref} id="vision-2030" aria-label={data.sectionAriaLabel}>
      <div className="dz-vision__inner">
        <div className="dz-vision__head">
          <div className="dz-vision__head-left">
            <span className="dz-eyebrow">{data.eyebrow}</span>
            <h2 className="dz-vision__title">{data.titleLeading} <em>{data.titleAccent}</em></h2>
            <p className="dz-vision__lead">{data.lead}</p>
          </div>
        </div>

        <div className="dz-vision__grid">
          {data.pillars.map((p, i) => (
            <article key={i} className="dz-vision__pillar">
              <div className="dz-vision__pillar-top">
                <span className="dz-vision__pillar-icon">{pillarIcons[i]}</span>
                <div className="dz-vision__pillar-heading">
                  <span className="dz-vision__pillar-num">{p.num}</span>
                  <h3 className="dz-vision__pillar-title">{p.title}</h3>
                </div>
              </div>
              <p className="dz-vision__pillar-mandate">{p.mandate}</p>
              <p className="dz-vision__pillar-delivery" dangerouslySetInnerHTML={{ __html: p.delivery }} />
              <div className="dz-vision__pillar-metric">
                <span className="dz-vision__pillar-metric-num">{p.metric.num}</span>
                <span className="dz-vision__pillar-metric-label">{p.metric.label}</span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   8 · Products — Tabbed Switcher (TwinMs · Operate / TwinMaq · Sell).
   Info column (act badge, name, tagline, features, hero ring + sparklines,
   CTA) + browser frame with FULL uncropped product UI screenshot.
   Both products are CUSTOM-DELIVERED (not SaaS). Brand colors: TwinMs teal,
   TwinMaq aqua.
   ────────────────────────────────────────────────────────────────────── */
function Products() {
  const ref = useReveal();
  // TwinMaq is the default tab — strongest visual + leads with pre-sales appeal.
  const [active, setActive] = useState('twinmaq');

  const ARROW = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/></svg>
  );
  const LOCK = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 11V7a7 7 0 0 0-14 0v4" strokeLinecap="round" strokeLinejoin="round"/><rect x="5" y="11" width="14" height="10" rx="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
  );

  // Product data (taglines, industries, features, ring stats, sparkline paths,
  // browser config) lives in content/home.js as window.DZ_HOME.products.items.
  const data = window.DZ_HOME.products;
  const products = data.items;
  const current = products[active];

  return (
    <section className="dz-prod dz-reveal" ref={ref} aria-label={data.sectionAriaLabel}>
      <div className="dz-prod__inner">
        <div className="dz-prod__head">
          <div className="dz-prod__head-left">
            <span className="dz-eyebrow">{data.eyebrow}</span>
            <h2 className="dz-prod__title">{data.titleLeading} <em>{data.titleAccent}</em> {data.titleTrailing}</h2>
          </div>
        </div>

        <div className="dz-prod__frame">
          <div className="dz-prod__tabs">
            <div className="dz-prod__tab-pills">
              {Object.entries(products).map(([key, p]) => (
                <button
                  key={key}
                  className={`dz-prod__tab dz-prod__tab--${key === 'twinms' ? 'teal' : 'aqua'}${active === key ? ' dz-prod__tab--active' : ''}`}
                  onClick={() => setActive(key)}
                >
                  <span className="dz-prod__tab-dot" />
                  {p.tabLabel}
                </button>
              ))}
            </div>
            {/* Active product logo replaces the old "Both demos" CTA.
                Uses the white-bg version so it reads cleanly inside the white glass panel.
                Per-logo height tuned so both products feel the same visual scale. */}
            <span key={`active-logo-${active}`} className="dz-prod__active-logo-mark">
              <img
                className="dz-prod__active-logo"
                src={current.logoTopBar.src}
                alt={current.logoTopBar.alt}
                style={{ height: `${current.logoTopBar.height}px` }}
              />
            </span>
          </div>

          <div className={`dz-prod__body ${current.colorClass}`}>
            <div className="dz-prod__info" key={`info-${active}`}>
              {/* TOP GROUP — logo, tagline, fused pills (industries + features) */}
              <div className="dz-prod__info-top">
                {/* Info-column logo: clean PNG with transparent bg + soft colored glow
                    (teal for TwinMs, aqua for TwinMaq) — no white panel here. */}
                <img
                  className="dz-prod__logo"
                  src={current.logo.src}
                  alt={current.logo.alt}
                  style={{ height: `${current.logo.height}px` }}
                />
                <p className="dz-prod__tagline" dangerouslySetInnerHTML={{ __html: current.tagline }} />
                <ul className="dz-prod__pills">
                  {current.industries.map((ind, i) => (
                    <li key={`ind-${i}`} className="dz-prod__pill dz-prod__pill--industry">{ind}</li>
                  ))}
                  {current.features.map((f, i) => (
                    <li key={`feat-${i}`} className="dz-prod__pill">{f}</li>
                  ))}
                </ul>
              </div>

              {/* BOTTOM GROUP — stats + CTA */}
              <div className="dz-prod__info-bottom">
                <div className="dz-prod__stats">
                  <div className="dz-prod__stats-hero">
                    <div className="dz-prod__ring">
                      <svg viewBox="0 0 64 64">
                        <circle className="dz-prod__ring-bg" cx="32" cy="32" r="28" />
                        <circle
                          className="dz-prod__ring-fg"
                          cx="32" cy="32" r="28"
                          style={{ '--ring-target': current.ring.dashFill }}
                        />
                      </svg>
                      <div className="dz-prod__ring-num">
                        <strong><AnimatedNumber target={current.ring.target} delay={150} /></strong>
                        <span>{current.ring.unit}</span>
                      </div>
                    </div>
                    <div className="dz-prod__hero-text">
                      <span className="dz-prod__hero-label">{current.ring.label}</span>
                      <span className="dz-prod__hero-sub">{current.ring.sub}</span>
                    </div>
                  </div>
                  <div className="dz-prod__stats-list">
                    {current.sparks.map((s, i) => (
                      <div key={i} className="dz-prod__stats-item">
                        <div className="dz-prod__stats-top">
                          <span className="dz-prod__stats-label">{s.label}</span>
                          <span className="dz-prod__stats-num">
                            <AnimatedNumber
                              target={s.target}
                              decimals={s.decimals}
                              prefix={s.prefix}
                              delay={400 + i * 250}
                            />
                            {s.suffix && <small>{s.suffix}</small>}
                          </span>
                        </div>
                        <svg className="dz-prod__spark" viewBox="0 0 100 16" preserveAspectRatio="none">
                          <path className="dz-prod__spark-area" d={s.area} />
                          <path className="dz-prod__spark-line" d={s.path} pathLength="100" />
                          <circle className="dz-prod__spark-dot" cx={s.dot.x} cy={s.dot.y} r="1.8" />
                        </svg>
                      </div>
                    ))}
                  </div>
                </div>

                <a href={data.ctaHref} className="dz-prod__cta">
                  {data.ctaLabel}
                  {ARROW}
                </a>
              </div>
            </div>

            <div className="dz-prod__stage-wrap">
              <div className="dz-prod__browser">
                <div className="dz-prod__browser-bar">
                  <div className="dz-prod__browser-dots">
                    <span className="dz-prod__browser-dot dz-prod__browser-dot--red" />
                    <span className="dz-prod__browser-dot dz-prod__browser-dot--yellow" />
                    <span className="dz-prod__browser-dot dz-prod__browser-dot--green" />
                  </div>
                  <div className="dz-prod__browser-url">
                    {LOCK}
                    {current.browser.url}
                  </div>
                  <div className="dz-prod__browser-menu">···</div>
                </div>
                <div className="dz-prod__browser-screen">
                  <img src={current.browser.img} alt={current.browser.alt} loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   9 · FeaturedCaseStudy — wide visual + floating overlay card
   ────────────────────────────────────────────────────────────────────── */
function FeaturedCase() {
  const ref = useReveal();
  return (
    <section className="dz-section dz-reveal" ref={ref} aria-label="Featured case study">
      <div className="dz-section__inner">
        <span className="dz-eyebrow">FEATURED PROJECT</span>
        <h2 className="dz-h2">Digital Twin delivery for a <em>Vision&nbsp;2030 anchor</em> project</h2>
        <p className="dz-lead">
          A representative engagement showing how Design Zone delivered the full lifecycle &mdash;
          from EIR authoring to TwinMs handover.
          {/* DR. ESRAA APPROVAL NEEDED — replace with real anchor case study */}
        </p>
        <div className="dz-case__frame">
          <div className="dz-case__visual">
            <span className="dz-case__visual-label">PROJECT VISUAL PLACEHOLDER</span>
            <div className="dz-case__overlay">
              <div className="dz-bezel">
                <div className="dz-bezel__core">
                  <span className="dz-case__sector">Mixed-Use Development &middot; Riyadh</span>
                  <h3 className="dz-case__name">Anchor Case Study Title</h3>
                  <p className="dz-case__scope">
                    Full BIM lifecycle delivery: EIR authoring, ISO&nbsp;19650 standards setup,
                    LOD&nbsp;300 multi-discipline modeling, clash detection, 4D scheduling, and
                    TwinMs digital twin handover.
                  </p>
                  <div className="dz-case__outcome">
                    <div className="dz-case__outcome-num">47%</div>
                    <div className="dz-case__outcome-label">
                      clash reduction vs. baseline,<br/>delivered 6 weeks ahead of schedule
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   10 · WhyDesignZone — Spotlight auto-carousel.
   5 differentiators rotate every 4s in the spotlight slot. Right-rail pills
   show all 5; click to jump + restart timer. Bottom strip displays the
   real BSI Associate Consultant + Autodesk ATC credential badges.
   ────────────────────────────────────────────────────────────────────── */
function WhyDZ() {
  const ref = useReveal();
  const data = window.DZ_HOME.whyDZ;
  const slides = data.slides;
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);

  // Auto-rotate every 4s. Reset on user interaction.
  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActive((a) => (a + 1) % slides.length);
    }, 4000);
  };

  useEffect(() => {
    startTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const goTo = (i) => {
    setActive(i);
    startTimer();
  };

  return (
    <section className="dz-why dz-reveal" ref={ref} aria-label={data.sectionAriaLabel}>
      <div className="dz-why__inner">
        <div className="dz-why__head">
          <div className="dz-why__head-left">
            <span className="dz-eyebrow">{data.eyebrow}</span>
            <h2 className="dz-why__title">{data.titleLeading} <em>{data.titleAccent}</em> {data.titleTrailing}</h2>
            <div className="dz-why__meta">
              <span className="dz-why__meta-num">{data.metaNum}</span>
              <span className="dz-why__meta-label">{data.metaLabel}</span>
            </div>
          </div>
        </div>

        <div className="dz-why__stage">
          <div className="dz-why__spotlight">
            {slides.map((s, i) => (
              <div key={i} className={`dz-why__slide${i === active ? ' dz-why__slide--active' : ''}`}>
                <span className="dz-why__slide-step">{s.step}</span>
                <h3 className="dz-why__slide-big" dangerouslySetInnerHTML={{ __html: s.big }} />
                <p className="dz-why__slide-claim" dangerouslySetInnerHTML={{ __html: s.claim }} />
                <p className="dz-why__slide-detail" dangerouslySetInnerHTML={{ __html: s.detail }} />
              </div>
            ))}
          </div>

          <div className="dz-why__rail">
            {slides.map((s, i) => (
              <button
                key={i}
                className={`dz-why__pill${i === active ? ' dz-why__pill--active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Show ${s.pillLabel}`}
              >
                <span className="dz-why__pill-text">
                  <span className="dz-why__pill-h">{s.pillLabel}</span>
                  <span className="dz-why__pill-sub">{s.pillSub}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="dz-why__strip">
          <span className="dz-why__strip-label">{data.stripLabel}</span>
          <div className="dz-why__strip-badges">
            {data.stripBadges.map((b, i) => (
              <div key={i} className="dz-why__strip-frame">
                <img src={b.src} alt={b.alt} loading="lazy" decoding="async" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   11 · FaqSection — minimal hairline accordion
   ────────────────────────────────────────────────────────────────────── */
function Faq() {
  const ref = useReveal();
  const data = window.DZ_HOME.faq;
  // The question list is always shown; each answer opens via its own <details>.
  return (
    <section className="dz-section dz-reveal dz-faq" ref={ref} aria-label={data.sectionAriaLabel}>
      <div className="dz-section__inner">
        <span className="dz-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-h2">{data.titleLeading} <em>{data.titleAccent}</em></h2>
        <p className="dz-lead">{data.lead}</p>
        <div className="dz-faq__list" id="dz-faq-list">
          {data.items.map((it, i) => (
            <details key={i} className="dz-faq-item">
              <summary>{it.q}</summary>
              {/* aHtml: optional rich-text answer (contextual links) — same
                  innerHTML pattern GeoAnswer uses for data.subtitle. Falls
                  back to the plain-text `a` when absent. */}
              {it.aHtml
                ? <div className="dz-faq-item__body" dangerouslySetInnerHTML={{ __html: it.aHtml }} />
                : <div className="dz-faq-item__body">{it.a}</div>}
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   12 · CtaStrip — centered title, "what happens next" steps left,
   inquiry form right. Vertical step animation triggers when steps
   scroll into view (numbers pop in sequentially, lines draw between
   them, text fades in stagger). Endorsements: BSI ACP + Autodesk ATC.
   ────────────────────────────────────────────────────────────────────── */

/* ────────────────────────────────────────────────────────────
   Footer — site-wide bottom (5-col sitemap + hover-popover offices)
   ──────────────────────────────────────────────────────────── */
/* Footer moved to js/Footer.jsx — it's shared across every page.
   Footer data still lives in content/footer.js (window.DZ_FOOTER). */

// eslint-disable-next-line no-unused-vars

/* ── Composite home sections ── */
function HomeSections() {
  // Lean 7-section home (excluding hero). Sections moved to dedicated pages:
  //   WhatIsBIM, ServicesGrid → /services
  //   Stats                   → fold into Hero or WhyDZ later
  //   FeaturedCase            → /case-studies
  //   Faq                     → /faq or footer
  return (
    <>
      <GeoAnswer />
      <FeaturedProjects />
      <Vision2030Block />
      <Products />
      <WhyDZ />
      <Faq />
      <CtaStrip />
      <Footer />
      {/* Moved to dedicated pages — kept here for reference:
        <ClientLogos />  (replaced by FeaturedProjects; its numbers strip now lives there)
        <WhatIsBIM />
        <ServicesGrid />
        <Stats />
        <FeaturedCase />
      */}
    </>
  );
}

window.HomeSections = HomeSections;
